// Authoritative taper schedule algorithm.
// Exponential decay with per-period reduction `r` such that the target
// on the end date is at or below `effectiveZero`. The final scheduled
// day is then forced to 0 (full cessation).
//
// This module is imported by both the server (functions/api/taper/*)
// and the client (live preview while the user adjusts the duration).
// The server's persisted schedule is authoritative; the client value
// is for chart preview only.

import { EFFECTIVE_ZERO_MG, TAPER_BAND } from './constants';

export interface TaperDay {
  dayIndex: number;
  date: string; // YYYY-MM-DD
  targetMg: number;
  isHold: boolean;
  holdReason?: string;
}

export interface TaperHold {
  fromDate: string;
  days: number;
  reason: string;
}

export interface TaperInput {
  baselineMg: number;
  startDate: string; // YYYY-MM-DD
  endDate?: string;
  weeks?: number;
  periodDays?: number; // default 7
  effectiveZeroMg?: number; // default 0.5
  holds?: TaperHold[];
}

export type TaperWarning =
  | { kind: 'aggressive'; rPerWeek: number; suggestedWeeks: number }
  | { kind: 'cold_turkey'; rPerWeek: number; suggestedWeeks: number }
  | { kind: 'low_baseline'; baselineMg: number }
  | { kind: 'too_long'; weeks: number };

export interface TaperPlan {
  planId: string;
  baselineMg: number;
  reductionPerPeriod: number;
  reductionPerWeek: number;
  periodDays: number;
  weeks: number;
  startDate: string;
  endDate: string;
  days: TaperDay[];
  warnings: TaperWarning[];
}

const MS_PER_DAY = 86_400_000;

export function computeTaperSchedule(input: TaperInput): TaperPlan {
  if (!Number.isFinite(input.baselineMg) || input.baselineMg <= 0) {
    throw new Error('baselineMg must be > 0');
  }

  const periodDays = input.periodDays ?? 7;
  const effectiveZero = input.effectiveZeroMg ?? EFFECTIVE_ZERO_MG;

  let totalDays: number;
  let endDate: string;
  if (input.endDate) {
    totalDays = daysBetween(input.startDate, input.endDate);
    if (totalDays <= 0) throw new Error('endDate must be after startDate');
    endDate = input.endDate;
  } else if (input.weeks && input.weeks > 0) {
    totalDays = Math.round(input.weeks * periodDays);
    endDate = addDays(input.startDate, totalDays);
  } else {
    throw new Error('Provide endDate or weeks');
  }
  const weeks = totalDays / periodDays;

  // Solve for r: baseline * (1-r)^(totalDays/periodDays) = effectiveZero
  const ratio = effectiveZero / input.baselineMg;
  let r: number;
  if (ratio >= 1) {
    r = 0;
  } else {
    r = 1 - Math.pow(ratio, periodDays / totalDays);
  }
  r = clamp(r, 0, 0.999);

  const days: TaperDay[] = [];
  for (let n = 0; n <= totalDays; n++) {
    const raw = input.baselineMg * Math.pow(1 - r, n / periodDays);
    const target = n === totalDays ? 0 : roundMg(Math.max(raw, 0));
    days.push({
      dayIndex: n,
      date: addDays(input.startDate, n),
      targetMg: target,
      isHold: false,
    });
  }

  if (input.holds?.length) {
    for (const h of input.holds) {
      applyHold(days, h, periodDays, effectiveZero);
    }
  }

  const rPerWeek = 1 - Math.pow(1 - r, 7 / periodDays);
  const warnings: TaperWarning[] = [];
  if (rPerWeek > TAPER_BAND.AGGRESSIVE_MAX) {
    warnings.push({
      kind: 'cold_turkey',
      rPerWeek,
      suggestedWeeks: suggestWeeksForRate(input.baselineMg, 0.2, periodDays, effectiveZero),
    });
  } else if (rPerWeek > TAPER_BAND.STANDARD_MAX) {
    warnings.push({
      kind: 'aggressive',
      rPerWeek,
      suggestedWeeks: suggestWeeksForRate(input.baselineMg, 0.2, periodDays, effectiveZero),
    });
  }
  if (input.baselineMg < 5) {
    warnings.push({ kind: 'low_baseline', baselineMg: input.baselineMg });
  }
  if (weeks > 52) {
    warnings.push({ kind: 'too_long', weeks });
  }

  return {
    planId: cryptoRandomUUID(),
    baselineMg: input.baselineMg,
    reductionPerPeriod: r,
    reductionPerWeek: rPerWeek,
    periodDays,
    weeks,
    startDate: input.startDate,
    endDate,
    days,
    warnings,
  };
}

function applyHold(
  days: TaperDay[],
  h: TaperHold,
  periodDays: number,
  effectiveZero: number,
): void {
  const startIdx = days.findIndex((d) => d.date === h.fromDate);
  if (startIdx < 0) return;
  const heldMg = days[startIdx].targetMg;
  if (heldMg <= 0) return;

  // Freeze K days at heldMg.
  const K = Math.min(h.days, days.length - startIdx - 1);
  for (let i = startIdx; i < startIdx + K; i++) {
    days[i].targetMg = heldMg;
    days[i].isHold = true;
    days[i].holdReason = h.reason;
  }

  // Re-fit r over the remaining days so endDate doesn't slip.
  const remainingDays = days.length - 1 - (startIdx + K);
  if (remainingDays <= 0 || heldMg <= effectiveZero) return;
  const newR = 1 - Math.pow(effectiveZero / heldMg, periodDays / remainingDays);
  for (let n = 1; n <= remainingDays; n++) {
    const idx = startIdx + K + n;
    const raw = heldMg * Math.pow(1 - newR, n / periodDays);
    days[idx].targetMg = idx === days.length - 1 ? 0 : roundMg(Math.max(raw, 0));
  }
}

function suggestWeeksForRate(
  baselineMg: number,
  targetRWeek: number,
  periodDays: number,
  effectiveZero: number,
): number {
  if (baselineMg <= effectiveZero) return 1;
  const lhs = Math.pow(1 - targetRWeek, periodDays / 7);
  // baseline * lhs^(totalDays/periodDays) = effectiveZero
  // => totalDays = log(eZ/baseline) / log(lhs) * periodDays
  const totalDays = (Math.log(effectiveZero / baselineMg) / Math.log(lhs)) * periodDays;
  return Math.max(1, Math.ceil(totalDays / 7));
}

// Convert leaf grams to estimated 7-OH mg.
export function leafGramsToMg7oh(grams: number, mgPerGram: number): number {
  return roundMg(grams * mgPerGram);
}

// Convert extract: mL * concentration_mg/mL = mg of 7-OH.
export function extractToMg7oh(mL: number, concentrationMgPerMl: number): number {
  return roundMg(mL * concentrationMgPerMl);
}

/**
 * Compute mg of mitragynine-equivalent (MEQ) for a dose. Used by the
 * optional MEQ surface in Insights (Gap 6 of the powder-tracking sprint).
 *
 * Math:
 *   - leaf:       amount_g × mgPerGramMitragynine
 *   - tablet_7oh: amount_mg × mitragyninePer7oh (default 4)
 *   - extract:    amount_mL × concentrationMgPerMl × mitragyninePer7oh
 *
 * Returns null for doses where MEQ isn't meaningful (extract without a
 * concentration). The leaf path is the most defensible — it's what's
 * literally in the bag — while tablet/extract paths use an inverse-
 * metabolic-ratio conversion to keep the unit comparable across forms.
 */
export function computeMgMitragynine(args: {
  form: 'tablet_7oh' | 'leaf' | 'extract';
  amount: number;
  amountUnit: 'mg' | 'g' | 'mL';
  concentrationMgPerMl?: number | null;
  /** Default ~14 mg/g — see shared/constants.ts MG_MITRAGYNINE_PER_GRAM_LEAF. */
  mgPerGramMitragynine: number;
  /** Default ~4 — see shared/constants.ts MITRAGYNINE_PER_MG_7OH. */
  mitragyninePer7oh: number;
}): number | null {
  if (args.form === 'leaf') {
    if (args.amountUnit !== 'g') return null;
    return roundMg(args.amount * args.mgPerGramMitragynine);
  }
  if (args.form === 'tablet_7oh') {
    if (args.amountUnit !== 'mg') return null;
    return roundMg(args.amount * args.mitragyninePer7oh);
  }
  if (args.form === 'extract') {
    if (args.amountUnit !== 'mL') return null;
    if (!args.concentrationMgPerMl || args.concentrationMgPerMl <= 0) return null;
    return roundMg(args.amount * args.concentrationMgPerMl * args.mitragyninePer7oh);
  }
  return null;
}

export function computeMg7oh(args: {
  form: 'tablet_7oh' | 'leaf' | 'extract';
  amount: number;
  amountUnit: 'mg' | 'g' | 'mL';
  concentrationMgPerMl?: number | null;
  mgPerGramLeaf: number;
}): number {
  if (args.form === 'tablet_7oh') {
    if (args.amountUnit !== 'mg') {
      throw new Error('tablet_7oh requires amount_unit=mg');
    }
    return roundMg(args.amount);
  }
  if (args.form === 'leaf') {
    if (args.amountUnit !== 'g') {
      throw new Error('leaf requires amount_unit=g');
    }
    return leafGramsToMg7oh(args.amount, args.mgPerGramLeaf);
  }
  if (args.form === 'extract') {
    if (args.amountUnit !== 'mL') {
      throw new Error('extract requires amount_unit=mL');
    }
    if (!args.concentrationMgPerMl || args.concentrationMgPerMl <= 0) {
      throw new Error('extract requires concentrationMgPerMl > 0');
    }
    return extractToMg7oh(args.amount, args.concentrationMgPerMl);
  }
  throw new Error(`Unknown form: ${args.form satisfies never}`);
}

function roundMg(x: number): number {
  return Math.round(x * 10) / 10;
}
function clamp(x: number, lo: number, hi: number): number {
  return Math.min(Math.max(x, lo), hi);
}
function addDays(d: string, n: number): string {
  const dt = new Date(`${d}T00:00:00Z`);
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}
function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / MS_PER_DAY);
}

// Works in browsers, Workers, and recent Node. Falls back to a tiny
// RFC 4122 v4 implementation if crypto.randomUUID is unavailable.
function cryptoRandomUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
